import { ELEMENT_TYPES, type ElementNodeDto, type ElementType } from './element.schema';

// Which element may sit inside which — the rule the whole drag experience rests on, and the one a tree
// saved as code must keep too. It used to live only in the storefront's catalog, so the builder obeyed it
// and nothing else did: a tree written by hand could put a heading on the page itself or a column outside
// a row, and the server stored it. It lives here now, read by the catalog and checked on every save.

/** The types that only make sense inside one particular parent, and so never appear in a generic box's list. */
const PARENT_BOUND: readonly ElementType[] = ['section', 'column'];

/** Everything a generic box — a section, a column, a container — will take. */
export const ANY_ELEMENT: readonly ElementType[] = ELEMENT_TYPES.filter((type) => !PARENT_BOUND.includes(type));

/** What a page's roots may hold. The root is not an element, so it has no entry of its own below. */
export const ROOT_ACCEPTS: readonly ElementType[] = ['section'];

/** What a row holds: columns, and nothing else — the one rule that keeps the grid arithmetic honest. */
export const COLUMNS: readonly ElementType[] = ['column'];

// Everything a generic box takes except one type, for a list that must not hold a copy of itself.
const anyBut = (type: ElementType): readonly ElementType[] => ANY_ELEMENT.filter((entry) => entry !== type);

/** What each element will hold; a type with no entry holds nothing at all. */
export const ELEMENT_ACCEPTS: Readonly<Partial<Record<ElementType, readonly ElementType[]>>> = {
  section: ANY_ELEMENT,
  row: COLUMNS,
  column: ANY_ELEMENT,
  container: ANY_ELEMENT,
  slideshow: ANY_ELEMENT,
  tabs: ANY_ELEMENT,
  marquee: ANY_ELEMENT,
  'block-link': ANY_ELEMENT,
  popup: ANY_ELEMENT,
  form: ANY_ELEMENT,
  'collection-list': anyBut('collection-list'),
  'product-list': anyBut('product-list'),
  'product-rail': anyBut('product-rail'),
  'review-list': anyBut('review-list'),
  bundle: anyBut('bundle'),
  'product-scope': anyBut('product-scope')
};

// Names what one element type will hold, which is nothing for a leaf.
export const acceptedTypes = (type: ElementType): readonly ElementType[] => ELEMENT_ACCEPTS[type] ?? [];

// Reports whether a parent — null for the page itself — will hold a child of one type.
export const acceptsChild = (parent: ElementType | null, child: ElementType): boolean =>
  (parent === null ? ROOT_ACCEPTS : acceptedTypes(parent)).includes(child);

/** One node placed where its parent will not hold it: where it is, and what is wrong in words. */
export type NestingFault = { path: (string | number)[]; message: string };

/** The most faults one walk reports, so a badly broken tree is described, not dumped. */
const MAX_FAULTS = 50;

// Walks one list of siblings, adding a fault for each child its parent will not hold.
function walkNesting(nodes: readonly ElementNodeDto[], parent: ElementNodeDto | null, at: (string | number)[], faults: NestingFault[]): void {
  nodes.forEach((node, index) => {
    if (faults.length >= MAX_FAULTS) return;
    const path = [...at, index];
    if (!acceptsChild(parent?.type ?? null, node.type)) {
      const where = parent ? `a ${parent.type}` : 'the page itself';
      faults.push({ path, message: `A ${node.type} cannot go inside ${where}` });
    }
    walkNesting(node.children, node, [...path, 'children'], faults);
  });
}

// Names every node of a tree that stands where its parent will not hold it, outermost first.
export function nestingFaults(roots: readonly ElementNodeDto[]): NestingFault[] {
  const faults: NestingFault[] = [];
  walkNesting(roots, null, ['roots'], faults);
  return faults;
}
